import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { Address } from "@/Common/Models/Contacts/Address";
import { Email } from "@/Common/Models/Contacts/Email";
import { GenderIdentity as GenderIdentityEnum } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns as PreferredPronounsEnum } from "@/Common/Models/Individuals/Enums/PreferredPronouns";

type FirstName = "FirstName";
type LastName = "LastName";
type PreferredName = "PreferredName";
type PreferredPronouns = "PreferredPronouns";
type SpecifiedPreferredPronouns = "SpecifiedPreferredPronouns";
type DOB = "DOB";
type Prefix = "Prefix";
type GenderLookupId = "GenderLookupId";
type GenderIdentity = "GenderIdentity";
type HealthCardNumber = "HealthCardNumber";
type HealthCardExpiryDate = "HealthCardExpiryDate";
type NAMEdit = "NAMEdit";
type NAMExpiryEdit = "NAMExpiryEdit";
type IsNAMValid = "IsNAMValid";
type SelectedHealthCardRegionLookupId = "SelectedHealthCardRegionLookupId";
type AvailablePrefixLookups = "AvailablePrefixLookups";
type SelectedPrefixLookupId = "SelectedPrefixLookupId";

type Addresses = "Addresses";
type SelectedCountryLookupId = "SelectedCountryLookupId";
type SelectedRegionLookupId = "SelectedRegionLookupId";
type AvailableCountryLookupIds = "AvailableCountryLookupIds";
type AvailableRegionLookupIds = "AvailableRegionLookupIds";
type CivicAddressEdit = "CivicAddressEdit";
type CityEdit = "CityEdit";
type PostalCodeEdit = "PostalCodeEdit";
type IsPostalCodeValid = "IsPostalCodeValid";

type Phone1Edit = "Phone1Edit";
type Phone1TypeLookupId = "Phone1TypeLookupId";
type Phone1ContactTypeLookupId = "Phone1ContactTypeLookupId";
type Phone1Extension = "Phone1Extension";
type IsPhone1Valid = "IsPhone1Valid";
type Phone1NotificationsEnabled = "Phone1NotificationsEnabled";

type Emails = "Emails";
type Phone2Edit = "Phone2Edit";
type Phone2TypeLookupId = "Phone2TypeLookupId";
type Phone2ContactTypeLookupId = "Phone2ContactTypeLookupId";
type Phone2Extension = "Phone2Extension";
type IsPhone2Valid = "IsPhone2Valid";
type Phone2NotificationsEnabled = "Phone2NotificationsEnabled";

type EmailEdit = "EmailEdit";
type EmailTypeByLookup = "EmailTypeByLookup";
type IsEmailValid = "IsEmailValid";
type IsOkToContact = "IsOkToContact";
type EmailNotificationsEnabled = "EmailNotificationsEnabled";

class RelativeCreationModel {
    public FirstName: string;
    public LastName: string;
    public PreferredName: string;
    public PreferredPronouns: PreferredPronounsEnum;
    public SpecifiedPreferredPronouns: string;
    public DOB: Date;
    public SelectedPrefixLookupId: string;
    public GenderLookupId: string;
    public GenderIdentity: GenderIdentityEnum;
    public NAMEdit: string;
    public NAMExpiryEdit: Date;
    public NAM: string;
    public NAMExpiry: Date;
    public IsNAMValid: boolean;
    public SelectedHealthCardRegionLookupId: string;

    public CivicAddressEdit: string;
    public CityEdit: string;
    public PostalCodeEdit: string;
    public IsPostalCodeValid: boolean;

    public SelectedCountryLookupId: string;
    public SelectedRegionLookupId: string;
    public AvailablePrefixLookups: FlatLookupView[];

    public Phone1Edit: string;
    public Phone1Extension: string;
    public Phone1NotificationsEnabled: boolean;
    public IsPhone1Valid: boolean;
    public Phone1TypeLookupId: string;
    public Phone1ContactTypeLookupId: string;

    public Phone2Edit: string;
    public Phone2Extension: string;
    public Phone2NotificationsEnabled: boolean;
    public IsPhone2Valid: boolean;
    public Phone2TypeLookupId: string;
    public Phone2ContactTypeLookupId: string;

    public EmailEdit: string;
    public IsEmailValid: boolean;
    public EmailNotificationsEnabled: boolean;
    public IsOkToContact: boolean;
    public EmailTypeByLookup: string;

    public static getHealthCardRegionLookupId(model: IRelativeCreationModel): string {
        return cacheStore.ClinicSettings.EnableHealthCardValidation
            ? model.get("SelectedHealthCardRegionLookupId")
            : model.get("SelectedRegionLookupId");
    }

    public static getDefault(): RelativeCreationModel {
        var maleLookup = cacheStore.FindLookupByLookupCode("MALE").LookupID;
        return {
            FirstName: "",
            LastName: "",
            PreferredName: "",
            PreferredPronouns: null,
            SpecifiedPreferredPronouns: "",
            GenderLookupId: maleLookup,
            GenderIdentity: null,
            SelectedCountryLookupId: cacheStore.GetDefaultCountryForActiveSite()?.LookupID,
            SelectedRegionLookupId: cacheStore.GetDefaultRegionForActiveSite()?.LookupID,
            CivicAddressEdit: "",
            CityEdit: "",
            PostalCodeEdit: "",
            NAMEdit: "",
            NAMExpiryEdit: null,
            NAM: "",
            NAMExpiry: null,
            SelectedHealthCardRegionLookupId: cacheStore.GetDefaultRegionForActiveSite()?.LookupID,
            DOB: null,
            AvailablePrefixLookups: cacheStore.GetPrefixIndividualLookups(),
            SelectedPrefixLookupId: "",
            IsPostalCodeValid: true,
            IsNAMValid: true,
            Phone1Edit: "",
            Phone1TypeLookupId: cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE").LookupID,
            Phone1ContactTypeLookupId: cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE").LookupID,
            Phone1Extension: "",
            IsPhone1Valid: true,
            Phone1NotificationsEnabled: false,
            Phone2Edit: "",
            Phone2TypeLookupId: cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE").LookupID,
            Phone2ContactTypeLookupId: cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE").LookupID,
            Phone2Extension: "",
            IsPhone2Valid: true,
            Phone2NotificationsEnabled: false,
            EmailEdit: "",
            IsEmailValid: true,
            EmailTypeByLookup: cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE").LookupID,
            IsOkToContact: true,
            EmailNotificationsEnabled: false
        };
    }

    public static getDefaultImmutable(): IRelativeCreationModel {
        return Immutable.fromJS(RelativeCreationModel.getDefault()) as IRelativeCreationModel;
    }
}

interface IRelativeCreationModel {
    get(key: FirstName): string;
    set(key: FirstName, value: string): this;

    get(key: LastName): string;
    set(key: LastName, value: string): this;

    get(key: PreferredName): string;
    set(key: PreferredName, value: string): this;

    get(key: PreferredPronouns): PreferredPronounsEnum;
    set(key: PreferredPronouns, value: PreferredPronounsEnum): this;

    get(key: SpecifiedPreferredPronouns): string;
    set(key: SpecifiedPreferredPronouns, value: string): this;

    get(key: Prefix): string;
    set(key: Prefix, value: string): this;

    get(key: HealthCardNumber): string;
    set(key: HealthCardNumber, value: string): this;

    get(key: HealthCardExpiryDate): Date;
    set(key: HealthCardExpiryDate, value: Date): this;

    get(key: Addresses): Immutable.List<Address>;
    set(key: Addresses, value: Immutable.List<Address>): this;

    get(key: Emails): Immutable.List<Email>;
    set(key: Emails, value: Immutable.List<Email>): this;

    get(key: GenderLookupId): string;
    set(key: GenderLookupId, value: string): this;

    get(key: GenderIdentity): GenderIdentityEnum;
    set(key: GenderIdentity, value: GenderIdentityEnum): this;

    get(key: CivicAddressEdit): string;
    set(key: CivicAddressEdit, value: string): this;

    get(key: CityEdit): string;
    set(key: CityEdit, value: string): this;

    get(key: PostalCodeEdit): string;
    set(key: PostalCodeEdit, value: string): this;

    get(key: NAMEdit): string;
    set(key: NAMEdit, value: string): this;

    get(key: NAMExpiryEdit): Date;
    set(key: NAMExpiryEdit, value: Date): this;

    get(key: SelectedHealthCardRegionLookupId): string;
    set(key: SelectedHealthCardRegionLookupId, value: string): this;

    get(key: DOB): Date;
    set(key: DOB, value: Date): this;

    get(key: AvailablePrefixLookups): Immutable.List<FlatLookupView>
    set(key: AvailablePrefixLookups, value: Immutable.List<FlatLookupView>): this;

    get(key: SelectedPrefixLookupId): string;
    set(key: SelectedPrefixLookupId, value: string): this

    get(key: AvailableCountryLookupIds): Immutable.List<FlatLookupView>
    set(key: AvailableCountryLookupIds, value: Immutable.List<FlatLookupView>): this;

    get(key: SelectedCountryLookupId): string;
    set(key: SelectedCountryLookupId, value: string): this;

    get(key: AvailableRegionLookupIds): Immutable.List<FlatLookupView>
    set(key: AvailableRegionLookupIds, value: Immutable.List<FlatLookupView>): this;

    get(key: SelectedRegionLookupId): string;
    set(key: SelectedRegionLookupId, value: string): this;

    get(key: IsPostalCodeValid): boolean;
    set(key: IsPostalCodeValid, value: boolean): this;

    get(key: IsNAMValid): boolean;
    set(key: IsNAMValid, value: boolean): this;

    get(key: Phone1Edit): string;
    set(key: Phone1Edit, value: string): this;

    get(key: Phone1TypeLookupId): string;
    set(key: Phone1TypeLookupId, value: string): this;

    get(key: Phone1ContactTypeLookupId): string;
    set(key: Phone1ContactTypeLookupId, value: string): this;

    get(key: Phone1Extension): string;
    set(key: Phone1Extension, value: string): this;

    get(key: IsPhone1Valid): boolean;
    set(key: IsPhone1Valid, value: boolean): this;

    get(key: Phone1NotificationsEnabled): boolean;
    set(key: Phone1NotificationsEnabled, value: boolean): this;

    get(key: Phone2Edit): string;
    set(key: Phone2Edit, value: string): this;

    get(key: Phone2TypeLookupId): string;
    set(key: Phone2TypeLookupId, value: string): this;

    get(key: Phone2ContactTypeLookupId): string;
    set(key: Phone2ContactTypeLookupId, value: string): this;

    get(key: Phone2Extension): string;
    set(key: Phone2Extension, value: string): this;

    get(key: IsPhone2Valid): boolean;
    set(key: IsPhone2Valid, value: boolean): this;

    get(key: Phone2NotificationsEnabled): boolean;
    set(key: Phone2NotificationsEnabled, value: boolean): this;

    get(key: EmailEdit): string;
    set(key: EmailEdit, value: string): this;

    get(key: IsEmailValid): boolean;
    set(key: IsEmailValid, value: boolean): this;

    get(key: EmailTypeByLookup): string;
    set(key: EmailTypeByLookup, value: string): this;

    get(key: IsOkToContact): boolean;
    set(key: IsOkToContact, value: boolean): this;

    get(key: EmailNotificationsEnabled): boolean;
    set(key: EmailNotificationsEnabled, value: boolean): this;
}

export { IRelativeCreationModel, RelativeCreationModel };
